import cloneDeep from 'lodash/cloneDeep';
import { EDoorWindowSide, EDoorWindowType, IWindowData } from '../data/model/model';
import { modelManager } from '../manager/entityManager';
import { globalStores, TGlobalStores } from '../stores';
import { getNextFlipState, getUniqueId } from '../utils';
import { getToolType } from '../controller/controller2d/utils';
import { EEntityType, EOperateMode } from '../types';
import { EOperationType } from '../types/operation';
import { IPoint2d } from '../math/IPoint2d';
import { syncCenterTypeToDistanceType } from '../utils/window';

export class WindowDoorOperation {
  private _modelStore: TGlobalStores['modelStore'];
  private _faceStore: TGlobalStores['faceStore'];
  private _editorStateStore: TGlobalStores['editorStateStore'];
  private _history: TGlobalStores['history'];

  constructor(gs: TGlobalStores) {
    this._modelStore = gs.modelStore;
    this._faceStore = gs.faceStore;
    this._editorStateStore = gs.editorStateStore;
    this._history = gs.history;
  }

  private $history_addWindowDoor(windowDoorData: IWindowData) {
    this._history.startOperation();

    this._modelStore.addWindowData(windowDoorData);
    this._history.completeOperation(EOperationType.WindowDoorAdd);
  }

  private $history_deleteWindowDoor(windowId: string) {
    this._history.startOperation();

    this._modelStore.removeWindowData(windowId);
    this._history.completeOperation(EOperationType.WindowDoorDel);
  }

  private $history_modifyWindowDoor(windowId: string, windowData: Partial<IWindowData>) {
    this._history.startOperation();

    this._modelStore.updateWindowData(windowId, windowData);
    this._history.completeOperation(EOperationType.WindowDoorModify);
  }

  addWindowDoor(windowDoorData: IWindowData) {
    this.$history_addWindowDoor(windowDoorData);
    this.reRender(windowDoorData.id);
  }

  deleteWindowDoor(windowId: string) {
    this.$history_deleteWindowDoor(windowId);
    this.reRender(windowId);
  }

  deleteWindowDoors(windowIds: string[]) {
    this._history.startOperation();
    for (const id of windowIds) {
      this._modelStore.removeWindowData(id);
    }
    this._history.completeOperation(EOperationType.WindowDoorDel);

    for (const id of windowIds) {
      this.reRender(id);
    }
  }

  modifyWindowDoor(windowId: string, windowData: Partial<IWindowData>) {
    this.$history_modifyWindowDoor(windowId, windowData);
    this.reRender(windowId);
  }

  modifyWindowDoorCenter(windowId: string, center: IPoint2d) {
    const windowData = this._modelStore.getWindowData(windowId);
    if (!windowData) return;

    const newWD = syncCenterTypeToDistanceType({ ...windowData, center });
    const distance = newWD.distance;
    this.modifyWindowDoor(windowId, { distance });
  }

  private reRender(windowId: string | string[]) {
    const needUpdateWindowIds = Array.isArray(windowId) ? windowId : [windowId];
    for (const needUpdateWindowId of needUpdateWindowIds) {
      // 重新渲染门窗
      modelManager.windowManager.updateEntity(needUpdateWindowId);
    }

    // 重新渲染墙面
    this._faceStore.refreshFaceDatas(); // TODO: 待优化，只刷新相关墙面
    modelManager.faceManager.update();

    // 重新渲染高亮框
    modelManager.selectedRerender();
  }

  replaceDoorWindowModel(windowIds: string[], modelId: string) {
    const modelData = this._modelStore.getModel(modelId);
    if (!modelData) return;
    this._history.startOperation();

    for (const windowId of windowIds) {
      const windowData = this._modelStore.getWindowData(windowId);
      if (!windowData) continue;

      const nextData = cloneDeep(windowData);
      nextData.modelId = modelId;
      this._modelStore.updateWindowData(windowId, nextData);
      this.reRender(windowId);
    }
    this._history.completeOperation(EOperationType.WindowDoorModify);

    return true;
  }

  copyDoorWindow(windowId: string) {
    const windowData = this._modelStore.getWindowData(windowId);
    if (!windowData) return;

    const copyWindowData = cloneDeep(windowData);
    copyWindowData.relativeWallInfo.relativeWallId = '';
    copyWindowData.id = getUniqueId();

    this._editorStateStore.windowDoorVrtualData = copyWindowData;

    globalStores.operateInfo = {
      mode: EOperateMode.DrawDoorWindow,
      toolType: getToolType(copyWindowData.type),
      extraState: {
        copying: true
      }
    };

    globalStores.editorStateStore.setSelected({
      entityId: '',
      type: EEntityType.EMPTY
    });
  }

  flipWindowDoor(windowId: string) {
    const windowDoorData = this._modelStore.getWindowData(windowId);
    if (!windowDoorData) return;

    switch (windowDoorData.type) {
      case EDoorWindowType.doubleDoor:
      case EDoorWindowType.window:
      case EDoorWindowType.frenchWindow: {
        const side = windowDoorData.side === EDoorWindowSide.left ? EDoorWindowSide.right : EDoorWindowSide.left;
        this.modifyWindowDoor(windowId, { side });
        break;
      }
      default: {
        const side = windowDoorData.side ?? EDoorWindowSide.left;
        const flip = !!windowDoorData.flip;

        this.modifyWindowDoor(windowId, getNextFlipState(side, flip));
      }
    }
  }

  generateDoorWindowArray(
    windowData: IWindowData,
    grid: { countX: number; countY: number; stepX: IPoint2d; stepY: IPoint2d }
  ) {
    this._history.startOperation();
    for (let i = 0; i < grid.countX; i++) {
      for (let j = 0; j < grid.countY; j++) {
        if (i === 0 && j === 0) {
          continue;
        }

        const newWindowData = syncCenterTypeToDistanceType({
          ...cloneDeep(windowData),
          id: getUniqueId(),
          center: {
            x: windowData.center.x + grid.stepX.x * i + grid.stepY.x * j,
            y: windowData.center.y + grid.stepX.y * i + grid.stepY.y * j
          }
        });

        this._modelStore.addWindowData(newWindowData);
      }
    }
    this._history.completeOperation(EOperationType.WindowDoorAdd);
    modelManager.windowManager.update();
    this._faceStore.refreshFaceDatas();
    modelManager.faceManager.update();
  }
}
